import api from '@/api';

/** Account-wide team channel (notify-on-save + manual posts). */
export const TEAM_CHAT_THREAD_ID = '__team__';

export const DM_THREAD_PREFIX = 'dm|';
export const GROUP_THREAD_PREFIX = 'group|';

export type TeamChatThreadKind = 'dm' | 'team' | 'group';

export function buildDmThreadId(uidA: string, uidB: string): string {
  const sorted = [uidA, uidB].sort();
  return `${DM_THREAD_PREFIX}${sorted[0]}|${sorted[1]}`;
}

export function isDmThreadId(threadId: string): boolean {
  return threadId.startsWith(DM_THREAD_PREFIX);
}

export function buildGroupThreadId(groupId: string): string {
  return `${GROUP_THREAD_PREFIX}${groupId}`;
}

export function isGroupThreadId(threadId: string): boolean {
  return threadId.startsWith(GROUP_THREAD_PREFIX);
}

export function parseGroupThreadId(threadId: string): string | null {
  if (!isGroupThreadId(threadId)) return null;
  const id = threadId.slice(GROUP_THREAD_PREFIX.length).trim();
  return id || null;
}

export function buildChatThreadUrl(threadId: string): string {
  return `/chat?thread=${encodeURIComponent(threadId)}`;
}

export type TeamChatContentKind = 'task' | 'bookmark' | 'note';

export type TeamChatContentRef = {
  kind: TeamChatContentKind;
  id: string;
  title?: string;
  projectSid?: string;
};

export type TeamChatMessage = {
  id: string;
  projectSid: string;
  authorUid: string;
  authorEmail?: string;
  body: string;
  mentionUids?: string[];
  contentRefs?: TeamChatContentRef[];
  createdAt: number;
  source?: 'manual' | 'notify_on_save';
};

export type TeamChatThread = {
  projectSid: string;
  projectName: string;
  threadKind: TeamChatThreadKind;
  peerUid?: string;
  peerEmail?: string;
  memberCount?: number;
  groupIcon?: string;
  lastMessage?: {
    body: string;
    authorEmail?: string;
    createdAt: number;
  };
  unreadCount: number;
  hasMentionUnread: boolean;
};

export type TeamChatGroupMember = {
  uid: string;
  name: string;
  email?: string;
};

export async function fetchTeamChatThreads(): Promise<{
  threads: TeamChatThread[];
  teammateCount: number;
}> {
  try {
    const res = await api.get<{
      status: string;
      threads?: TeamChatThread[];
      teammateCount?: number;
    }>('/team-chat/threads');
    if (res.data?.status !== 'success') {
      return { threads: [], teammateCount: 0 };
    }
    return {
      threads: res.data.threads ?? [],
      teammateCount: res.data.teammateCount ?? 0,
    };
  } catch {
    return { threads: [], teammateCount: 0 };
  }
}

export async function fetchTeamChatMessages(
  threadId: string,
  opts?: { limit?: number; before?: number }
): Promise<TeamChatMessage[]> {
  const res = await api.get<{ status: string; messages?: TeamChatMessage[] }>(
    `/team-chat/threads/${encodeURIComponent(threadId)}/messages`,
    { params: opts }
  );
  if (res.data?.status !== 'success') return [];
  return res.data.messages ?? [];
}

export async function searchTeamChatMessages(
  threadId: string,
  query: string
): Promise<{ messages: TeamChatMessage[]; truncated: boolean }> {
  const res = await api.get<{
    status: string;
    messages?: TeamChatMessage[];
    truncated?: boolean;
  }>(`/team-chat/threads/${encodeURIComponent(threadId)}/search`, {
    params: { q: query },
  });
  if (res.data?.status !== 'success') return { messages: [], truncated: false };
  return {
    messages: res.data.messages ?? [],
    truncated: res.data.truncated ?? false,
  };
}

export async function postTeamChatMessage(
  threadId: string,
  body: {
    body: string;
    mentionUids?: string[];
    contentRefs?: TeamChatContentRef[];
    broadcast?: boolean;
  }
): Promise<TeamChatMessage | null> {
  const res = await api.post<{ status: string; message?: TeamChatMessage }>(
    `/team-chat/threads/${encodeURIComponent(threadId)}/messages`,
    body
  );
  if (res.data?.status !== 'success') return null;
  return res.data.message ?? null;
}

export async function markTeamChatThreadRead(threadId: string): Promise<void> {
  await api.patch(`/team-chat/threads/${encodeURIComponent(threadId)}/read`);
}

export async function hideTeamChatDmThread(threadId: string): Promise<boolean> {
  const res = await api.post<{ status: string }>(
    `/team-chat/threads/${encodeURIComponent(threadId)}/hide`
  );
  return res.data?.status === 'success';
}

export async function createTeamChatGroup(payload: {
  name: string;
  memberUids: string[];
  icon?: string;
}): Promise<{ threadId: string; groupName: string } | null> {
  const res = await api.post<{
    status: string;
    threadId?: string;
    group?: { name: string };
    message?: string;
  }>('/team-chat/groups', payload);
  if (res.data?.status !== 'success' || !res.data.threadId) return null;
  return { threadId: res.data.threadId, groupName: res.data.group?.name ?? payload.name };
}

export async function fetchTeamChatGroup(
  groupId: string
): Promise<{
  group: { id?: string; name: string; memberUids: string[]; createdBy: string; icon?: string };
  members: TeamChatGroupMember[];
  canAdminister: boolean;
} | null> {
  const res = await api.get<{
    status: string;
    group?: { id?: string; name: string; memberUids: string[]; createdBy: string; icon?: string };
    members?: TeamChatGroupMember[];
    canAdminister?: boolean;
  }>(`/team-chat/groups/${encodeURIComponent(groupId)}`);
  if (res.data?.status !== 'success' || !res.data.group) return null;
  return {
    group: res.data.group,
    members: res.data.members ?? [],
    canAdminister: res.data.canAdminister ?? false,
  };
}

export async function updateTeamChatGroup(
  groupId: string,
  payload: { name?: string; memberUids?: string[]; icon?: string }
): Promise<{ groupName: string; groupIcon?: string } | null> {
  const res = await api.patch<{
    status: string;
    group?: { name: string; icon?: string };
    message?: string;
  }>(`/team-chat/groups/${encodeURIComponent(groupId)}`, payload);
  if (res.data?.status !== 'success') return null;
  return {
    groupName: res.data.group?.name ?? payload.name ?? '',
    groupIcon: res.data.group?.icon,
  };
}

export async function deleteTeamChatGroup(groupId: string): Promise<boolean> {
  const res = await api.delete<{ status: string }>(
    `/team-chat/groups/${encodeURIComponent(groupId)}`
  );
  return res.data?.status === 'success';
}

export async function leaveTeamChatGroup(groupId: string): Promise<boolean> {
  const res = await api.post<{ status: string }>(
    `/team-chat/groups/${encodeURIComponent(groupId)}/leave`
  );
  return res.data?.status === 'success';
}

export async function fetchNotifyTeamDefault(projectSid: string): Promise<{
  defaultChecked: boolean;
  teammateCount: number;
  sharedProject: boolean;
  notifyTeamDefault: 'always' | 'never' | 'ask';
}> {
  const res = await api.get<{
    status: string;
    defaultChecked?: boolean;
    teammateCount?: number;
    sharedProject?: boolean;
    notifyTeamDefault?: 'always' | 'never' | 'ask';
  }>('/team-chat/notify-default', { params: { projectSid } });
  if (res.data?.status !== 'success') {
    return { defaultChecked: false, teammateCount: 0, sharedProject: false, notifyTeamDefault: 'ask' };
  }
  return {
    defaultChecked: res.data.defaultChecked ?? false,
    teammateCount: res.data.teammateCount ?? 0,
    sharedProject: res.data.sharedProject ?? false,
    notifyTeamDefault: res.data.notifyTeamDefault ?? 'ask',
  };
}

export type TeamChatWebhook = {
  url: string;
  enabled: boolean;
  kind: string;
};

export async function fetchTeamChatWebhook(): Promise<TeamChatWebhook | null> {
  const res = await api.get<{ status: string; webhook?: TeamChatWebhook | null }>(
    '/team-chat/webhook'
  );
  if (res.data?.status !== 'success') return null;
  return res.data.webhook ?? null;
}

export async function updateTeamChatWebhook(body: {
  url?: string;
  enabled?: boolean;
}): Promise<void> {
  await api.put('/team-chat/webhook', body);
}

export async function testTeamChatWebhook(): Promise<{ ok: boolean; message?: string }> {
  const res = await api.post<{ status: string; message?: string }>('/team-chat/webhook/test');
  return {
    ok: res.data?.status === 'success',
    message: res.data?.message,
  };
}
